import { useState } from "react";
import { UpgradePrompt } from "@/components/billing/UpgradePrompt";
import { useEntitlements } from "@/lib/entitlements";
import { useTenant } from "@/lib/hooks/useTenant";
import { gateReasonFromError, type GateReason } from "@/lib/plan-gates";
import {
  Download,
  Copy,
  Check,
  Puzzle,
  ShieldCheck,
  Zap,
  ShoppingBag,
  Sparkles,
  Layers,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase, isDemoMode } from "@/lib/supabase";

/** Extensao: a captura de sessao do Mercado Livre (token de pareamento) vale
 *  para todos os planos; gerar links pela extensao nas outras lojas e PRO. */
export function ExtensaoPage(): JSX.Element {
  const { data: tenantBilling } = useTenant();
  const entitlements = useEntitlements(tenantBilling?.tenant);
  const [tokenGate, setTokenGate] = useState<GateReason | null>(null);
  const [pairingToken, setPairingToken] = useState("");
  const [loadingToken, setLoadingToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  async function handleGenerateToken(): Promise<void> {
    setLoadingToken(true);
    setTokenError(null);

    if (isDemoMode()) {
      // Mock de token no modo demonstração
      setTimeout(() => {
        const fakeToken =
          "demo" + Array.from({ length: 60 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
        setPairingToken(fakeToken);
        setLoadingToken(false);
      }, 400);
      return;
    }

    try {
      const { data, error } = await supabase.rpc("create_mercadolivre_extension_token");
      const gate = gateReasonFromError(error);
      setTokenGate(gate);
      if (gate) return;
      if (error) {
        throw new Error(error.message || "Erro ao gerar token.");
      }
      setPairingToken(data as string);
    } catch (err) {
      setTokenError(
        err instanceof Error ? err.message : "Não foi possível gerar o token de pareamento agora.",
      );
    } finally {
      setLoadingToken(false);
    }
  }

  async function handleCopyToken(): Promise<void> {
    if (!pairingToken) return;
    await navigator.clipboard.writeText(pairingToken);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2500);
  }

  return (
    <div className="max-w-4xl space-y-6">
      <PageHeader
        title="Extensão do Navegador"
        subtitle="Gere links de afiliado instantâneos direto nas páginas das lojas e sincronize sessões do Mercado Livre com o robô de vendas."
      />

      {!entitlements.hasExtension && <UpgradePrompt reason="extension" variant="inline" />}
      {tokenGate && <UpgradePrompt reason={tokenGate} variant="inline" />}

      {/* Banner de Download */}
      <Card className="relative overflow-hidden border-emerald-500/20 bg-gradient-to-r from-emerald-950/40 via-card to-background">
        <div className="absolute right-0 top-0 -mr-16 -mt-16 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
        <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-6 sm:p-8">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400">
              <Sparkles size={13} />
              <span>Versão 1.1.0 Estável</span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground">
              LucraLink Capture para Chrome & Edge
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Detecta produtos na Amazon, Mercado Livre, Shopee, Magalu e AliExpress.
              Gera links prontos com sua comissão e copia textos prontos para seus grupos de Telegram e WhatsApp.
            </p>
          </div>

          <div className="flex flex-col gap-2 w-full sm:w-auto">
            <a
              href="/downloads/lucralink-extension.zip"
              download="lucralink-extension.zip"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-500 px-5 py-3 text-sm font-semibold text-emerald-950 shadow hover:bg-emerald-400 transition-colors"
            >
              <Download size={18} />
              <span>Baixar Extensão (.zip)</span>
            </a>
            <span className="text-center text-xs text-muted-foreground">
              Chrome, Edge, Brave, Opera
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Token de Pareamento */}
      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="flex items-center gap-2 border-b border-border/50 pb-3">
            <ShieldCheck size={20} className="text-emerald-500" />
            <h3 className="font-semibold text-foreground">Token de Pareamento da Conta</h3>
          </div>

          <p className="text-sm text-muted-foreground">
            Conecte a extensão à sua conta LucraLink colando este token na aba <strong>Configurar</strong> da extensão. O token é válido por 30 minutos e conecta suas tags automaticamente.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            {pairingToken ? (
              <div className="relative flex-1">
                <Input
                  readOnly
                  value={pairingToken}
                  className="font-mono text-xs pr-10 bg-muted/30"
                />
                <button
                  type="button"
                  onClick={handleCopyToken}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title="Copiar token"
                >
                  {tokenCopied ? (
                    <Check size={16} className="text-emerald-400" />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
              </div>
            ) : null}

            <Button
              type="button"
              variant={pairingToken ? "outline" : "default"}
              onClick={handleGenerateToken}
              disabled={loadingToken}
              className="gap-2 sm:w-auto"
            >
              {loadingToken ? (
                "Gerando token..."
              ) : pairingToken ? (
                "Gerar Novo Token"
              ) : (
                <>
                  <Zap size={16} />
                  <span>Gerar Token de Pareamento</span>
                </>
              )}
            </Button>
          </div>

          {tokenCopied && (
            <p className="text-xs text-emerald-400 font-medium">
              ✓ Token copiado para a área de transferência! Cole na extensão para conectar.
            </p>
          )}

          {tokenError && (
            <p className="text-xs text-rose-400 font-medium">
              ⚠️ {tokenError}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Passo a Passo de Instalação */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Puzzle size={18} className="text-emerald-400" />
          <span>Como Instalar em 30 Segundos</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-card/50">
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/10 font-bold text-xs text-emerald-400 border border-emerald-500/30">
                  1
                </span>
                <h4 className="font-semibold text-sm text-foreground">Baixar e Extrair o ZIP</h4>
              </div>
              <p className="text-xs text-muted-foreground pl-10">
                Clique no botão de download acima e extraia a pasta do arquivo <code>lucralink-extension.zip</code> para um local de fácil acesso (ex.: sua pasta Documentos).
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/10 font-bold text-xs text-emerald-400 border border-emerald-500/30">
                  2
                </span>
                <h4 className="font-semibold text-sm text-foreground">Abrir chrome://extensions</h4>
              </div>
              <p className="text-xs text-muted-foreground pl-10">
                Digite <code>chrome://extensions</code> na barra do seu navegador (ou <code>edge://extensions</code> no Edge) e pressione Enter.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/10 font-bold text-xs text-emerald-400 border border-emerald-500/30">
                  3
                </span>
                <h4 className="font-semibold text-sm text-foreground">Ativar Modo do Desenvolvedor</h4>
              </div>
              <p className="text-xs text-muted-foreground pl-10">
                No canto superior direito da página de extensões, ligue a chave <strong>&quot;Modo do desenvolvedor&quot;</strong>.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/10 font-bold text-xs text-emerald-400 border border-emerald-500/30">
                  4
                </span>
                <h4 className="font-semibold text-sm text-foreground">Carregar sem compactação</h4>
              </div>
              <p className="text-xs text-muted-foreground pl-10">
                Clique no botão <strong>&quot;Carregar sem compactação&quot;</strong> no canto esquerdo e selecione a pasta descompactada da extensão. Pronto!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recursos e Diferenciais */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Layers size={18} className="text-emerald-400" />
          <span>Recursos da Extensão LucraLink</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-2 text-emerald-400">
                <Zap size={18} />
                <h4 className="font-semibold text-sm text-foreground">Link com 1 Clique</h4>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Ao abrir qualquer produto em loja suportada, a extensão identifica o produto e gera o link com sua tag na hora.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-2 text-amber-400">
                <ShoppingBag size={18} />
                <h4 className="font-semibold text-sm text-foreground">Post Pronto para Grupos</h4>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Gera texto promocional formatado com título, preço e link encurtado para envio instantâneo no WhatsApp e Telegram.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-2 text-blue-400">
                <ShieldCheck size={18} />
                <h4 className="font-semibold text-sm text-foreground">Sessão Mercado Livre</h4>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Captura segura da sua sessão logada para permitir que o bot gere links <code>meli.la</code> sem depender de API externa.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
